import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Package, ArrowLeft, Loader2, CheckCircle, XCircle, Clock, Truck, MapPin, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { WhatsAppCTA, buildQuoteContextMessage, WHATSAPP_MESSAGES } from './WhatsAppButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Checkout = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Shipping state
  const [shippingMethod, setShippingMethod] = useState(null); // 'pickup' | 'delivery'
  const [shippingRates, setShippingRates] = useState([]);
  const [loadingRates, setLoadingRates] = useState(false);
  const [selectedRate, setSelectedRate] = useState(null);
  const [shippingNotQuotable, setShippingNotQuotable] = useState(null);
  const [savingShipping, setSavingShipping] = useState(false);
  const [shippingConfirmed, setShippingConfirmed] = useState(false);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await axios.get(`${API}/orders/${orderId}`);
        const o = response.data;
        setOrder(o);
        // If already has a shipping method saved, restore it
        if (o.shipping_method) {
          setShippingMethod(o.shipping_method);
          setShippingConfirmed(true);
        }
      } catch (error) {
        toast.error('Orden no encontrada');
        navigate('/configurar');
      }
      setLoading(false);
    };
    fetchOrder();
  }, [orderId, navigate]);

  const [shippingErrors, setShippingErrors] = useState([]);

  const fetchShippingQuotes = async () => {
    setLoadingRates(true);
    setShippingRates([]);
    setSelectedRate(null);
    setShippingNotQuotable(null);
    setShippingErrors([]);
    try {
      const res = await axios.post(`${API}/shipping/quote`, { order_id: orderId });
      if (!res.data.auto_quotable) {
        setShippingNotQuotable(res.data.manual_reason || res.data.message);
      } else {
        setShippingRates(res.data.rates || []);
        setShippingErrors(res.data.errors || []);
      }
    } catch (err) {
      toast.error('Error al cotizar envío. Intenta de nuevo o contacta soporte.');
    }
    setLoadingRates(false);
  };

  const handleShippingMethodChange = (method) => {
    setShippingMethod(method);
    setShippingConfirmed(false);
    setSelectedRate(null);
    setShippingRates([]);
    setShippingNotQuotable(null);
    if (method === 'delivery') {
      fetchShippingQuotes();
    }
  };

  const confirmShipping = async () => {
    setSavingShipping(true);
    try {
      const payload = { shipping_method: shippingMethod };
      if (shippingMethod === 'delivery' && selectedRate) {
        payload.selected_rate = selectedRate;
      }
      const res = await axios.put(`${API}/orders/${orderId}/shipping-selection`, payload);
      // Refresh order to get updated total
      const orderRes = await axios.get(`${API}/orders/${orderId}`);
      setOrder(orderRes.data);
      setShippingConfirmed(true);
      toast.success(shippingMethod === 'pickup' ? 'Recoger en planta confirmado' : 'Envío seleccionado');
    } catch (err) {
      toast.error('Error al guardar método de envío');
    }
    setSavingShipping(false);
  };

  const handleMercadoPago = async () => {
    if (!shippingConfirmed) {
      toast.error('Primero selecciona un método de envío');
      return;
    }
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/checkout`, {
        order_id: orderId,
        payment_provider: 'mercado_pago',
      }, {
        headers: { 'Origin': window.location.origin },
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar el pago');
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (!order) return null;

  const isPaid = order.payment_status === 'paid';
  const shippingCost = order.shipping_cost || 0;
  const needsShipping = !order.shipping_method;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <Package className="w-8 h-8 text-accent" />
            <span className="text-xl font-bold text-primary">KAHA</span>
          </a>
          <span className="text-sm text-muted-foreground">Checkout</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button variant="ghost" onClick={() => navigate('/configurar')} className="mb-6 gap-2" data-testid="btn-back-config">
          <ArrowLeft className="w-4 h-4" /> Volver al configurador
        </Button>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Order Summary */}
          <div className="space-y-6">
            <Card className="shadow-soft">
              <CardHeader className="border-b">
                <CardTitle>Resumen de tu pedido</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Folio</span>
                    <span className="font-mono font-bold" data-testid="order-number">{order.order_number}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Dimensiones</span>
                    <span>{(order.dimensions.length / 10)} x {(order.dimensions.width / 10)} x {(order.dimensions.height / 10)} cm</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Material</span>
                    <span>{order.material?.name || '-'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Cantidad</span>
                    <span>{order.quantity?.toLocaleString()} cajas</span>
                  </div>
                  {order.printing?.has_printing && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Impresión</span>
                      <span>Sí ({order.printing.coverage_percent}%)</span>
                    </div>
                  )}
                </div>

                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>${order.subtotal?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">IVA</span>
                    <span>${order.iva_amount?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</span>
                  </div>
                  {shippingCost > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Envío ({order.shipping_carrier})</span>
                      <span>${shippingCost.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</span>
                    </div>
                  )}
                  {shippingConfirmed && shippingMethod === 'pickup' && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Envío</span>
                      <span className="text-green-600 font-medium">Recoger en planta</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t">
                    <span className="font-bold text-lg">Total</span>
                    <span className="text-2xl font-bold text-accent" data-testid="checkout-total">
                      ${order.total_amount?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                    </span>
                  </div>
                </div>

                <div className="bg-muted/50 rounded-lg p-4 text-sm">
                  <p className="font-semibold mb-1">Datos del cliente</p>
                  <p>{order.customer_name}</p>
                  <p className="text-muted-foreground">{order.customer_email}</p>
                  {order.customer_phone && <p className="text-muted-foreground">{order.customer_phone}</p>}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Shipping + Payment */}
          <div className="space-y-6">
            {/* Shipping Selection */}
            {(!isPaid || needsShipping) && (
              <Card className="shadow-soft">
                <CardHeader className="border-b bg-[#58655A] text-white">
                  <CardTitle className="flex items-center gap-2">
                    <Truck className="w-5 h-5" />
                    Método de entrega
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  {shippingConfirmed ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 p-4 border-2 border-green-500 rounded-lg bg-green-50">
                        <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
                        <div>
                          {shippingMethod === 'pickup' ? (
                            <p className="font-semibold text-green-800">Recoger en planta</p>
                          ) : (
                            <>
                              <p className="font-semibold text-green-800">
                                {order.shipping_carrier} — {order.shipping_service}
                              </p>
                              {order.shipping_delivery_days && (
                                <p className="text-sm text-green-700">{order.shipping_delivery_days} días hábiles</p>
                              )}
                              <p className="text-sm font-bold text-green-800">
                                ${order.shipping_cost?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setShippingConfirmed(false); setShippingMethod(null); }}
                        data-testid="btn-change-shipping"
                      >
                        Cambiar método
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => handleShippingMethodChange('pickup')}
                          className={`p-4 rounded-lg border-2 text-center transition-all ${
                            shippingMethod === 'pickup'
                              ? 'border-accent bg-accent/10'
                              : 'border-muted hover:border-accent/50'
                          }`}
                          data-testid="btn-shipping-pickup"
                        >
                          <MapPin className="w-6 h-6 mx-auto mb-2 text-accent" />
                          <p className="font-semibold text-sm">Recoger en planta</p>
                          <p className="text-xs text-muted-foreground mt-1">Sin costo adicional</p>
                        </button>
                        <button
                          onClick={() => handleShippingMethodChange('delivery')}
                          className={`p-4 rounded-lg border-2 text-center transition-all ${
                            shippingMethod === 'delivery'
                              ? 'border-accent bg-accent/10'
                              : 'border-muted hover:border-accent/50'
                          }`}
                          data-testid="btn-shipping-delivery"
                        >
                          <Truck className="w-6 h-6 mx-auto mb-2 text-accent" />
                          <p className="font-semibold text-sm">Envío a domicilio</p>
                          <p className="text-xs text-muted-foreground mt-1">Cotizar transportistas</p>
                        </button>
                      </div>

                      {/* Pickup confirmation */}
                      {shippingMethod === 'pickup' && (
                        <div className="space-y-3">
                          <div className="p-3 bg-muted/50 rounded-lg text-sm">
                            <p className="font-semibold">Dirección de la planta:</p>
                            <p className="text-muted-foreground">Océano Antártico de Linda Vista 606, León, Guanajuato, C.P. 37300</p>
                          </div>
                          <Button
                            onClick={confirmShipping}
                            disabled={savingShipping}
                            className="w-full bg-accent hover:bg-accent/90"
                            data-testid="btn-confirm-pickup"
                          >
                            {savingShipping ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            Confirmar: Recoger en planta
                          </Button>
                        </div>
                      )}

                      {/* Delivery rates */}
                      {shippingMethod === 'delivery' && (
                        <div className="space-y-3">
                          {loadingRates && (
                            <div className="flex items-center justify-center py-6 gap-3">
                              <Loader2 className="w-5 h-5 animate-spin text-accent" />
                              <span className="text-sm text-muted-foreground">Cotizando transportistas...</span>
                            </div>
                          )}

                          {shippingNotQuotable && (
                            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm space-y-3">
                              <p className="font-semibold text-amber-800">Cotización manual requerida</p>
                              <p className="text-amber-700">{shippingNotQuotable}</p>
                              <WhatsAppCTA
                                message={WHATSAPP_MESSAGES.shipping_help}
                                label="Solicitar atención por WhatsApp"
                                eventType="whatsapp_click_shipping_help"
                                page="checkout"
                                orderId={orderId}
                                variant="strong"
                              />
                            </div>
                          )}

                          {!loadingRates && shippingRates.length > 0 && (
                            <div className="space-y-2">
                              <p className="text-sm font-medium">Selecciona tu transportista:</p>
                              {shippingRates.map((rate, idx) => (
                                <button
                                  key={`${rate.carrier}-${rate.service}-${rate.total_price}`}
                                  onClick={() => setSelectedRate(rate)}
                                  className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                                    selectedRate === rate
                                      ? 'border-accent bg-accent/5'
                                      : 'border-muted hover:border-accent/30'
                                  }`}
                                  data-testid={`shipping-rate-${idx}`}
                                >
                                  <div className="flex justify-between items-center">
                                    <div>
                                      <p className="font-semibold text-sm capitalize">{rate.carrier}</p>
                                      <p className="text-xs text-muted-foreground">
                                        {rate.service_description || rate.service}
                                        {rate.delivery_days && ` · ${rate.delivery_days} días hábiles`}
                                      </p>
                                    </div>
                                    <span className="font-bold text-accent">
                                      ${rate.total_price.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                                    </span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}

                          {!loadingRates && shippingRates.length === 0 && !shippingNotQuotable && (
                            <div className="text-center py-4 space-y-3">
                              <p className="text-sm text-muted-foreground">
                                No se encontraron tarifas disponibles. Intenta más tarde o selecciona "Recoger en planta".
                              </p>
                              {shippingErrors.length > 0 && (
                                <p className="text-xs text-muted-foreground">
                                  Detalle: {shippingErrors.join(', ')}
                                </p>
                              )}
                              <button
                                onClick={fetchShippingQuotes}
                                className="text-sm text-accent hover:underline"
                                data-testid="btn-retry-shipping"
                              >
                                Reintentar cotización
                              </button>
                              <WhatsAppCTA
                                message={WHATSAPP_MESSAGES.shipping_help}
                                label="Cotizar por WhatsApp"
                                eventType="whatsapp_click_shipping_help"
                                page="checkout"
                                orderId={orderId}
                                variant="strong"
                              />
                            </div>
                          )}

                          {selectedRate && (
                            <Button
                              onClick={confirmShipping}
                              disabled={savingShipping}
                              className="w-full bg-accent hover:bg-accent/90"
                              data-testid="btn-confirm-delivery"
                            >
                              {savingShipping ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                              Confirmar envío: ${selectedRate.total_price.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                            </Button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Payment Methods */}
            <Card className={`shadow-soft transition-opacity ${!shippingConfirmed && !isPaid ? 'opacity-50 pointer-events-none' : ''}`}>
              <CardHeader className="border-b bg-primary text-white">
                <CardTitle>Métodos de pago</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {isPaid ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-green-700">Pago confirmado</h3>
                    <p className="text-muted-foreground mt-2">Esta orden ya está pagada.</p>
                  </div>
                ) : (
                  <>
                    {!shippingConfirmed && (
                      <p className="text-sm text-center text-amber-600 font-medium">
                        Selecciona un método de entrega antes de continuar al pago.
                      </p>
                    )}

                    <p className="text-sm text-muted-foreground text-center">
                      Serás redirigido a Mercado Pago para completar tu pago de forma segura.
                    </p>

                    {/* Mercado Pago Button */}
                    <Button
                      onClick={handleMercadoPago}
                      disabled={processingPayment || !shippingConfirmed}
                      className="w-full h-14 text-base gap-3 bg-[#009EE3] hover:bg-[#007EB5] text-white"
                      data-testid="btn-pay-mp"
                    >
                      {processingPayment ? (
                        <><Loader2 className="w-5 h-5 animate-spin" /> Redirigiendo a Mercado Pago...</>
                      ) : (
                        <>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9v-2h2v2zm4 0h-2v-2h2v2zm-6-4V9c0-.55.45-1 1-1h4c.55 0 1 .45 1 1v3h-6z"/></svg>
                          Pagar con Mercado Pago
                        </>
                      )}
                    </Button>

                    <div className="flex items-center gap-3 text-xs text-muted-foreground justify-center">
                      <span>Tarjetas</span>
                      <span>·</span>
                      <span>OXXO</span>
                      <span>·</span>
                      <span>SPEI</span>
                      <span>·</span>
                      <span>Transferencia</span>
                    </div>

                    <p className="text-xs text-center text-muted-foreground mt-4">
                      Tu información de pago está protegida por encriptación SSL de 256 bits.
                    </p>

                    <div className="pt-2 text-center">
                      <WhatsAppCTA
                        message={order ? buildQuoteContextMessage(order) : WHATSAPP_MESSAGES.general}
                        label="¿Tienes dudas? Escríbenos por WhatsApp"
                        eventType="whatsapp_click_checkout_help"
                        page="checkout"
                        orderId={orderId}
                      />
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

// Payment Success Page
export const PagoExitoso = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = searchParams.get('order_id');
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (orderId) {
      axios.get(`${API}/orders/${orderId}`).then(r => setOrder(r.data)).catch(() => {});
    }
  }, [orderId]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full shadow-floating" data-testid="payment-success-card">
        <CardContent className="p-8 text-center">
          <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Pago exitoso</h2>
          <p className="text-muted-foreground mb-2">Tu pedido ha sido recibido</p>
          {order && (
            <div className="bg-muted/50 rounded-lg p-4 text-sm my-4 text-left space-y-1">
              <p><span className="text-muted-foreground">Folio:</span> <strong>{order.order_number}</strong></p>
              <p><span className="text-muted-foreground">Total:</span> <strong>${order.total_amount?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</strong></p>
            </div>
          )}
          <p className="text-sm text-muted-foreground mb-6">
            Estamos validando tu archivo. Recibirás una confirmación por correo.
          </p>

          {/* Invoice CTA */}
          {order && !order.invoice_requested && (
            <div className="bg-muted/50 rounded-lg p-4 mb-4">
              <p className="text-sm text-muted-foreground mb-2">¿Necesitas factura?</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/facturacion/${orderId}`)}
                className="gap-2"
                data-testid="btn-request-invoice-success"
              >
                <FileText className="w-4 h-4" />
                Solicitar factura
              </Button>
            </div>
          )}
          {order?.invoice_requested && (
            <p className="text-xs text-green-600 mb-4">Factura solicitada</p>
          )}

          <div className="space-y-3">
            {order && (
              <Button variant="outline" onClick={() => navigate(`/rastreo/${order.order_number}`)} className="w-full gap-2" data-testid="btn-track-order">
                Rastrear mi pedido
              </Button>
            )}
            <Button onClick={() => navigate('/')} className="w-full bg-accent hover:bg-accent/90" data-testid="btn-back-home">
              Volver al inicio
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Payment Pending Page
export const PagoPendiente = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full shadow-floating" data-testid="payment-pending-card">
        <CardContent className="p-8 text-center">
          <div className="w-20 h-20 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-4">
            <Clock className="w-10 h-10 text-amber-600" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Pago pendiente</h2>
          <p className="text-muted-foreground mb-6">
            Tu pago está siendo procesado. Si pagaste en OXXO o por transferencia, puede tardar unas horas. Te notificaremos por correo cuando se confirme.
          </p>
          <div className="space-y-3">
            {orderId && (
              <Button onClick={() => navigate(`/checkout/${orderId}`)} className="w-full bg-accent hover:bg-accent/90">
                Ver mi pedido
              </Button>
            )}
            <Button onClick={() => navigate('/')} variant="outline" className="w-full">
              Volver al inicio
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Payment Failure Page
export const PagoFallido = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full shadow-floating" data-testid="payment-failed-card">
        <CardContent className="p-8 text-center">
          <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
            <XCircle className="w-10 h-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Pago rechazado</h2>
          <p className="text-muted-foreground mb-6">
            No pudimos procesar tu pago. Por favor intenta de nuevo o usa otro método de pago.
          </p>
          <div className="space-y-3">
            {orderId && (
              <Button onClick={() => navigate(`/checkout/${orderId}`)} className="w-full bg-accent hover:bg-accent/90">
                Reintentar pago
              </Button>
            )}
            <Button onClick={() => navigate('/configurar')} variant="outline" className="w-full">
              Volver al configurador
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Checkout;
